import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api, fileUrl } from "@/lib/api";
import { swallow } from "@/lib/log";
import { cn } from "@/lib/utils";
import EmptyState from "@/components/EmptyState";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Search, StickyNote, FileText, KeyRound, Image as ImageIcon, Film,
  Bell, BookText, Users, Heart, Archive, ChevronRight, Clock, Layers,
  TrendingUp, ShieldCheck, HeartPulse, Plane, Calendar as CalendarIcon, Mic,
} from "lucide-react";

/**
 * Unified Library page — Blueprint §5.
 *
 * Layout:
 *   1. Sticky search bar
 *   2. Tabs: All / Favorites / Archived
 *   3. Category grid (Notes, Documents, Photos, Videos, Passwords, Journal, Reminders, Legacy, Family)
 *   4. Recent items across types (compact list)
 */

const CATEGORIES = [
  { key: "notes",       label: "Notes",       icon: StickyNote, to: "/app/notes",       color: "text-amber-500 bg-amber-500/12"     },
  { key: "documents",   label: "Documents",   icon: FileText,   to: "/app/documents",   color: "text-emerald-500 bg-emerald-500/12" },
  { key: "photos",      label: "Photos",      icon: ImageIcon,  to: "/app/media?tab=photos", color: "text-purple-500 bg-purple-500/12" },
  { key: "videos",      label: "Videos",      icon: Film,       to: "/app/media?tab=videos", color: "text-pink-500 bg-pink-500/12"     },
  { key: "passwords",   label: "Passwords",   icon: KeyRound,   to: "/app/passwords",   color: "text-indigo-500 bg-indigo-500/12"   },
  { key: "journal",     label: "Journal",     icon: BookText,   to: "/app/journal",     color: "text-rose-500 bg-rose-500/12"       },
  { key: "reminders",   label: "Reminders",   icon: Bell,       to: "/app/reminders",   color: "text-orange-500 bg-orange-500/12"   },
  { key: "voice_notes", label: "Voice notes", icon: Mic,        to: "/app/voice-notes", color: "text-slate-600 bg-slate-600/12"     },
  { key: "investments", label: "Investments", icon: TrendingUp, to: "/app/investments", color: "text-emerald-600 bg-emerald-600/12" },
  { key: "insurance",   label: "Insurance",   icon: ShieldCheck,to: "/app/insurance",   color: "text-sky-500 bg-sky-500/12"         },
  { key: "medical",     label: "Medical",     icon: HeartPulse, to: "/app/medical",     color: "text-rose-600 bg-rose-600/12"       },
  { key: "travel",      label: "Travel",      icon: Plane,      to: "/app/travel",      color: "text-indigo-500 bg-indigo-500/12"   },
  { key: "calendar",    label: "Calendar",    icon: CalendarIcon, to: "/app/calendar",  color: "text-teal-600 bg-teal-600/12"       },
  { key: "legacy",      label: "Legacy",      icon: Layers,     to: "/app/legacy",      color: "text-slate-500 bg-slate-500/12"     },
  { key: "family",      label: "Family",      icon: Users,      to: "/app/household",   color: "text-teal-500 bg-teal-500/12"       },
];

const TYPE_META = {
  note:       { icon: StickyNote,  to: "/app/notes",     color: "text-amber-500 bg-amber-500/12"     },
  reminder:   { icon: Bell,        to: "/app/reminders", color: "text-orange-500 bg-orange-500/12"   },
  document:   { icon: FileText,    to: "/app/documents", color: "text-emerald-500 bg-emerald-500/12" },
  password:   { icon: KeyRound,    to: "/app/passwords", color: "text-indigo-500 bg-indigo-500/12"   },
  photo:      { icon: ImageIcon,   to: "/app/media?tab=photos", color: "text-purple-500 bg-purple-500/12" },
  video:      { icon: Film,        to: "/app/media?tab=videos", color: "text-pink-500 bg-pink-500/12" },
  journal:    { icon: BookText,    to: "/app/journal",   color: "text-rose-500 bg-rose-500/12"       },
  voice_note: { icon: Mic,         to: "/app/voice-notes", color: "text-slate-600 bg-slate-600/12"   },
  investment: { icon: TrendingUp,  to: "/app/investments", color: "text-emerald-600 bg-emerald-600/12" },
  insurance:  { icon: ShieldCheck, to: "/app/insurance",   color: "text-sky-500 bg-sky-500/12"         },
  medical:    { icon: HeartPulse,  to: "/app/medical",     color: "text-rose-600 bg-rose-600/12"       },
  travel:     { icon: Plane,       to: "/app/travel",      color: "text-indigo-500 bg-indigo-500/12"   },
};

export default function Library() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "all"); // all | favorites | archived
  const [q, setQ] = useState("");
  const [recent, setRecent] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [archived, setArchived] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        // Recent items aggregation across types (compact)
        const [notes, docs, photos, vids, pws, rems, jrls, invs, inss, meds, trvs, voxs, favs, arcs] = await Promise.all([
          api.get("/notes").catch(() => ({ data: [] })),
          api.get("/documents").catch(() => ({ data: [] })),
          api.get("/photos").catch(() => ({ data: [] })),
          api.get("/videos").catch(() => ({ data: [] })),
          api.get("/passwords").catch(() => ({ data: [] })),
          api.get("/reminders").catch(() => ({ data: [] })),
          api.get("/journal").catch(() => ({ data: [] })),
          api.get("/investments").catch(() => ({ data: [] })),
          api.get("/insurance").catch(() => ({ data: [] })),
          api.get("/medical").catch(() => ({ data: [] })),
          api.get("/travel").catch(() => ({ data: [] })),
          api.get("/voice-notes").catch(() => ({ data: [] })),
          api.get("/favorites").catch(() => ({ data: [] })),
          api.get("/archived").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;

        // Merge into a unified feed for "Recent"
        const feed = [];
        (notes.data || []).forEach((n) => feed.push({ ...n, _type: "note",       _title: n.title || n.content?.slice(0, 60) || "Note" }));
        (docs.data  || []).forEach((d) => feed.push({ ...d, _type: "document",   _title: d.title || "Document" }));
        (photos.data|| []).forEach((p) => feed.push({ ...p, _type: "photo",      _title: p.title || "Photo" }));
        (vids.data  || []).forEach((v) => feed.push({ ...v, _type: "video",      _title: v.title || "Video" }));
        (pws.data   || []).forEach((p) => feed.push({ ...p, _type: "password",   _title: p.label || "Password" }));
        (rems.data  || []).forEach((r) => feed.push({ ...r, _type: "reminder",   _title: r.text  || "Reminder" }));
        (jrls.data  || []).forEach((j) => feed.push({ ...j, _type: "journal",    _title: j.improved || j.original || j.raw_text || "Journal entry" }));
        (invs.data  || []).forEach((i) => feed.push({ ...i, _type: "investment", _title: i.title || "Investment" }));
        (inss.data  || []).forEach((i) => feed.push({ ...i, _type: "insurance",  _title: i.title || "Policy" }));
        (meds.data  || []).forEach((m) => feed.push({ ...m, _type: "medical",    _title: m.title || "Medical record" }));
        (trvs.data  || []).forEach((t) => feed.push({ ...t, _type: "travel",     _title: t.title || "Trip" }));
        (voxs.data  || []).forEach((v) => feed.push({ ...v, _type: "voice_note", _title: v.title || "Voice note" }));

        feed.sort((a, b) => {
          const at = new Date(a.updated_at || a.created_at || 0).getTime();
          const bt = new Date(b.updated_at || b.created_at || 0).getTime();
          return bt - at;
        });

        setRecent(feed);
        setFavorites(favs.data || []);
        setArchived(arcs.data || []);
        setCounts({
          notes:       (notes.data || []).length,
          documents:   (docs.data  || []).length,
          photos:      (photos.data|| []).length,
          videos:      (vids.data  || []).length,
          passwords:   (pws.data   || []).length,
          reminders:   (rems.data  || []).length,
          journal:     (jrls.data  || []).length,
          investments: (invs.data  || []).length,
          insurance:   (inss.data  || []).length,
          medical:     (meds.data  || []).length,
          travel:      (trvs.data  || []).length,
          voice_notes: (voxs.data  || []).length,
        });
      } catch (e) {
        swallow("Library")(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const setTabAndUrl = (t) => {
    setTab(t);
    const next = new URLSearchParams(sp);
    if (t === "all") next.delete("tab"); else next.set("tab", t);
    setSp(next, { replace: true });
  };

  const activeList = useMemo(() => {
    const query = q.trim().toLowerCase();
    let base;
    if (tab === "favorites") {
      base = favorites.map((it) => ({ ...it, _type: it.type, _title: it.title }));
    } else if (tab === "archived") {
      base = archived.map((it) => ({ ...it, _type: it.type, _title: it.title }));
    } else {
      base = recent;
    }
    if (!query) return base.slice(0, 60);
    return base.filter((it) => (it._title || "").toLowerCase().includes(query)).slice(0, 60);
  }, [tab, q, recent, favorites, archived]);

  const goSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/app/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <>
      <TopBar title="Library" subtitle="Everything important, one tap away" showHome={false} />
      <div className="px-4 pt-3 pb-6 space-y-4" data-testid="library-page">

        {/* Search bar */}
        <form onSubmit={goSearch} className="relative">
          <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            placeholder="Search anything..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-11 rounded-2xl bg-card border-border"
            data-testid="library-search-input"
          />
        </form>

        {/* Tabs */}
        <div className="flex items-center gap-1 rounded-full bg-card border border-border p-1" role="tablist" data-testid="library-tabs">
          {[
            { key: "all",       label: "All",       icon: Layers,   count: (counts.notes || 0) + (counts.documents || 0) + (counts.photos || 0) + (counts.videos || 0) + (counts.passwords || 0) + (counts.reminders || 0) + (counts.journal || 0) + (counts.investments || 0) + (counts.insurance || 0) + (counts.medical || 0) + (counts.travel || 0) + (counts.voice_notes || 0) },
            { key: "favorites", label: "Favorites", icon: Heart,    count: favorites.length },
            { key: "archived",  label: "Archived",  icon: Archive,  count: archived.length },
          ].map(({ key, label, icon: Icon, count }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTabAndUrl(key)}
              role="tab"
              aria-selected={tab === key}
              className={cn(
                "flex-1 h-9 rounded-full text-[12px] font-medium inline-flex items-center justify-center gap-1.5 transition",
                tab === key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              )}
              data-testid={`library-tab-${key}`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
              {count > 0 && (
                <Badge className="h-5 min-w-5 px-1.5 rounded-full bg-white/20 text-inherit border-0 text-[10px]">{count}</Badge>
              )}
            </button>
          ))}
        </div>

        {/* Category grid — only on 'All' tab */}
        {tab === "all" && (
          <div className="space-y-2" data-testid="library-categories">
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground px-1">Categories</div>
            <div className="grid grid-cols-3 gap-3">
              {CATEGORIES.map(({ key, label, icon: Icon, to, color }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => nav(to)}
                  className="rounded-2xl border border-border bg-card p-3 flex flex-col items-start gap-2 hover:bg-[hsl(var(--accent))] transition text-left active:scale-[0.98]"
                  data-testid={`library-cat-${key}`}
                >
                  <div className={cn("h-9 w-9 rounded-xl grid place-items-center", color)}>
                    <Icon className="h-4.5 w-4.5" strokeWidth={2} />
                  </div>
                  <div className="w-full">
                    <div className="font-display font-semibold text-[13px] leading-tight truncate">{label}</div>
                    <div className="text-[10px] text-muted-foreground">{counts[key] || 0} item{(counts[key] || 0) !== 1 ? "s" : ""}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Items list */}
        <div className="space-y-2" data-testid="library-items">
          <div className="flex items-center justify-between px-1">
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground inline-flex items-center gap-1.5">
              {tab === "favorites" ? <><Heart className="h-3 w-3" /> Favorites</> :
               tab === "archived" ? <><Archive className="h-3 w-3" /> Archived</> :
               <><Clock className="h-3 w-3" /> Recent</>}
            </div>
            <span className="text-[10px] text-muted-foreground">{activeList.length}</span>
          </div>

          {loading && (
            <div className="space-y-2">
              {[1,2,3].map((i) => (
                <div key={i} className="h-16 rounded-2xl bg-muted/50 animate-pulse" />
              ))}
            </div>
          )}

          {!loading && activeList.length === 0 && (
            <EmptyState
              icon={tab === "archived" ? Archive : tab === "favorites" ? Heart : Layers}
              title={tab === "archived" ? "Nothing archived" : tab === "favorites" ? "No favorites yet" : "Nothing saved yet"}
              subtitle={tab === "archived" ? "Items you archive show up here." :
                        tab === "favorites" ? "Tap the heart on any item to favorite it." :
                        "Start by saving a note or a document."}
            />
          )}

          {!loading && activeList.length > 0 && (
            <div className="space-y-2">
              {activeList.map((it) => {
                const meta = TYPE_META[it._type] || TYPE_META.note;
                const Icon = meta.icon;
                const isMedia = it._type === "photo" || it._type === "video";
                return (
                  <button
                    key={`${it._type}-${it.id}`}
                    type="button"
                    onClick={() => nav(meta.to)}
                    className="w-full rounded-2xl border border-border bg-card p-3 flex items-center gap-3 hover:bg-[hsl(var(--accent))] transition text-left active:scale-[0.995]"
                    data-testid={`library-item-${it._type}-${it.id}`}
                  >
                    {isMedia && it.file_url ? (
                      <div className="h-12 w-12 rounded-xl overflow-hidden bg-muted shrink-0">
                        <img
                          src={fileUrl(it.file_url)}
                          alt=""
                          className="h-full w-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      </div>
                    ) : (
                      <div className={cn("h-12 w-12 rounded-xl grid place-items-center shrink-0", meta.color)}>
                        <Icon className="h-5 w-5" strokeWidth={2} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{it._type}</span>
                        {it.is_favorite && <Heart className="h-3 w-3 text-rose-500 fill-current" />}
                      </div>
                      <div className="font-display font-semibold text-[13px] truncate mt-0.5">{it._title}</div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
